import { baseAPICreator } from './apiCreator';
import {
  NewCampaignRequestBody,
  UpdateCampaignRequestBody,
  CampaignProps,
  CreateCampaignResponse,
  CampaignListResponse,
  CampaignListQueryParams,
  CampaignDetailProps,
  CampaignDetailStatsApiResponse,
  CampaignItemProps,
  CampaignItemsRequestProps,
  CampaignRowsQueryParams,
  CampaignRowsResponse,
  StartCampaignProcessRequest,
  StartCampaignProcessResponse,
  ScoreUnscoredEpisodesRequest,
  ScoreUnscoredEpisodesResponse,
  UnscoredEpisodeImpressionsResponse,
  CampaignPreProcessingStats,
  CampaignPreviewMetricsResponse,
} from 'src/types/campaign';
import {
  AlignmentSummaryRequest,
  AlignmentSummaryResponse,
} from 'src/types/campaignReport';
import { RenameFolderResponse } from 'src/types/share';

const campaignApi = baseAPICreator('/campaign');

/** PATCH /campaign/folders/rename — moves folder share grants with the rename */
export const renameCampaignFolder = async (
  from: string,
  to: string
): Promise<RenameFolderResponse> => {
  const { data } = await campaignApi.patch<RenameFolderResponse>(
    '/folders/rename',
    { from, to }
  );
  return data;
};

/** Base host for campaign-data / process / status (paths under /api/v1/…). */
const CAMPAIGN_DATA_BASE_URL =
  process.env.REACT_APP_ENV === 'prod'
    ? 'https://api-campaign-processor.thebarometer.co/api'
    : 'https://api-dev-campaign-processor.thebarometer.co/api';
const campaignDataApi = baseAPICreator(
  '/v1',
  undefined,
  CAMPAIGN_DATA_BASE_URL
);

/** Optional extra CSV set for GET `/api/v1/download-campaign/:campaignId?include=`. */
export type CampaignDownloadInclude =
  | 'misaligned'
  | 'unmatched'
  | 'inclusion'
  | 'all';

/**
 * GET `/api/v1/download-campaign/:campaignId` — absolute URL for ZIP export
 * (same host as campaign processor). Omit `include` for processed CSV + stats.txt.
 */
export function getCampaignDownloadZipHref(
  campaignId: string,
  include?: CampaignDownloadInclude | ''
): string {
  const base = CAMPAIGN_DATA_BASE_URL.replace(/\/?$/, '');
  const url = `${base}/v1/download-campaign/${encodeURIComponent(campaignId)}`;
  if (!include) {
    return url;
  }
  return `${url}?include=${encodeURIComponent(include)}`;
}

export const getCampaignList = async (
  params?: CampaignListQueryParams
): Promise<CampaignListResponse> => {
  const queryParams = {
    page: params?.page ?? 0,
    pageSize: params?.pageSize ?? 10,
    sortBy: params?.sortBy ?? 'createdAt',
    sortOrder: params?.sortOrder ?? 'DESC',
    search: params?.search,
  };

  const queryString = new URLSearchParams(
    Object.entries(queryParams).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null) {
        acc[key] = String(value);
      }
      return acc;
    }, {} as Record<string, string>)
  ).toString();

  const { data } = await campaignApi.get(`?${queryString}`);
  return data;
};
export const getCampaignDetailStats = async (
  campaignId: string
): Promise<CampaignDetailStatsApiResponse> => {
  const { data } = await campaignDataApi.get(`/campaign-data/${campaignId}`);
  return data;
};

/**
 * GET /api/v1/campaign-rows/:campaignId
 * Query: view=shows|episodes (default shows), page, page_size (default 10, max 100), search
 */
export const getCampaignRows = async (
  campaignId: string,
  params?: CampaignRowsQueryParams
): Promise<CampaignRowsResponse> => {
  const pageSize = Math.min(100, Math.max(1, params?.page_size ?? 10));
  const query: Record<string, string> = {
    view: params?.view ?? 'shows',
    page: String(params?.page ?? 0),
    page_size: String(pageSize),
  };
  if (params?.search !== undefined && params.search !== '') {
    query.search = params.search;
  }
  if (params?.alignment_filter !== undefined) {
    query.alignment_filter = params.alignment_filter;
  }
  if (params?.sort_by !== undefined) {
    query.sort_by = params.sort_by;
  }
  if (params?.sort_order !== undefined) {
    query.sort_order = params.sort_order;
  }
  const qs = new URLSearchParams(query).toString();
  const { data } = await campaignDataApi.get(
    `/campaign-rows/${campaignId}?${qs}`
  );
  return data;
};

export const getCampaignDetail = async (
  campaignId: string
): Promise<CampaignDetailProps> => {
  const { data } = await campaignApi.get(`/${campaignId}`);
  return data;
};
export const getCampaignShowNames = async (
  campaignId: string
): Promise<string[]> => {
  // const { data } = await campaignApi.get(`/${campaignId}/shownames`);
  // return data;
  return [];
};

export const createCampaign = async (
  body: NewCampaignRequestBody
): Promise<CreateCampaignResponse> => {
  const formData = new FormData();
  const multiplePolicies = body.preferenceIds.length > 1;

  formData.append('name', body.name);
  if (multiplePolicies) {
    formData.append('preferenceIds', body.preferenceIds.join(','));
  } else {
    formData.append('preferenceId', body.preferenceIds[0] ?? '');
  }
  if (body.description) {
    formData.append('description', body.description);
  }
  formData.append('alignmentRequired', String(body.alignmentRequired));
  formData.append('file', body.file);
  formData.append('processingConfig', JSON.stringify(body.processingConfig));
  if (body.campaignStart) {
    formData.append('campaignStart', body.campaignStart);
  }
  if (body.campaignEnd) {
    formData.append('campaignEnd', body.campaignEnd);
  }

  // POST /campaign/bulk is one file × N policies. It does not declare the
  // split fields, so they must only be sent to POST /campaign.
  if (!multiplePolicies) {
    if (body.isMulti === true) {
      formData.append('isMulti', 'true');
    }
    if (body.columnName !== undefined && body.columnName !== '') {
      formData.append('columnName', body.columnName);
    }
    if (body.rowValues !== undefined && body.rowValues !== '') {
      formData.append('rowValues', body.rowValues);
    }
    if (body.folder !== undefined && body.folder !== '') {
      formData.append('folder', body.folder);
    }
  }
  if (body.dateColumnOverride === true) {
    formData.append('dateColumnOverride', 'true');
  }

  const { data } = await campaignApi.post(
    multiplePolicies ? '/bulk' : '/',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return data;
};

export const deleteCampaign = async (
  campaignId: string
): Promise<{ success: boolean }> => {
  const { data } = await campaignApi.delete(`/${campaignId}`);
  return data;
};

/** PUT /campaign/:campaignId */
export const updateCampaign = async (
  campaignId: string,
  body: UpdateCampaignRequestBody
): Promise<CampaignProps> => {
  const { data } = await campaignApi.put(`/${campaignId}`, body);
  return data;
};

/** PATCH /campaign/:campaignId/folder — body `{ folder: string }` (empty string for uncategorized) */
export const updateCampaignFolder = async (
  campaignId: string,
  folder: string
): Promise<void> => {
  await campaignApi.patch(`/${campaignId}/folder`, { folder });
};

/**
 * POST /api/v1/campaign/:campaignId/preview-metrics
 * Pre-processing stats for a campaign that has been created but not processed.
 */
export const getCampaignPreviewMetrics = async (
  campaignId: string
): Promise<CampaignPreProcessingStats> => {
  const { data } = await campaignDataApi.post<CampaignPreviewMetricsResponse>(
    `/campaign/${campaignId}/preview-metrics`
  );
  if (!data?.preProcessingStats) {
    throw new Error('Preview metrics were not returned');
  }
  return data.preProcessingStats;
};

export const startCampaignProcess = async (
  body: StartCampaignProcessRequest
): Promise<StartCampaignProcessResponse> => {
  const { data } = await campaignDataApi.post('/process-campaign', body);
  return data;
};

export const scoreUnscoredEpisodes = async (
  body: ScoreUnscoredEpisodesRequest
): Promise<ScoreUnscoredEpisodesResponse> => {
  const { data } = await campaignDataApi.post('/score-unscored-episodes', body);
  return data;
};

/**
 * POST /api/v1/reports/alignment-summary
 * Campaign-processor report payload for selected campaigns + preference/policy.
 */
export const getAlignmentSummary = async (
  body: AlignmentSummaryRequest
): Promise<AlignmentSummaryResponse> => {
  const { data } = await campaignDataApi.post<AlignmentSummaryResponse>(
    '/reports/alignment-summary',
    body
  );
  return data;
};

/**
 * GET /api/v1/unscored-episodes/:campaign_id
 * Returns `data` when `status === 'success'`, otherwise `[]`.
 */
export const getUnscoredEpisodeImpressions = async (
  campaignId: string
): Promise<number[]> => {
  const { data: body } =
    await campaignDataApi.get<UnscoredEpisodeImpressionsResponse>(
      `/unscored-episodes/${campaignId}`
    );
  if (body.status === 'success') {
    return body.data;
  }
  return [];
};

export const getCampaignItems = async (
  campaignId: string,
  body: CampaignItemsRequestProps,
  garmExceptions?: any
): Promise<{ count: number; items: CampaignItemProps[] }> => {
  let params = '';
  Object.keys(body).forEach((key, index) => {
    if (body[key as keyof CampaignItemsRequestProps] !== undefined) {
      params += `${index === 0 ? '?' : '&'}${key}=${
        body[key as keyof CampaignItemsRequestProps]
      }`;
    }
  });
  // const { data } = await campaignApi.post(`/${campaignId}/items${params}`, {
  //   garmExceptions,
  // });
  // return data;
  return { count: 0, items: [] };
};
